// Shared account controls: persistence and permission remain server-owned.
const safeUsername = value => typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/.test(value);
function node(tag, text, attributes = {}) {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
  return element;
}
function button(text, action) {
  const element = node("button", text, { type: "button", "data-block-action": action });
  element.style.minHeight = "44px";
  return element;
}
function failure(error) { return error?.message || "Account blocking could not be confirmed. Refresh and try again."; }
// Recovery only reopens authentication/context or rereads state; never a mutation.
export function accountBlockRecoveryKind(error) {
  const status = Number(error?.status || error?.statusCode);
  if (status === 401) return "sign-in";
  if (status === 409 && error?.code === "EPIC_ACCOUNT_CHANGED") return "account-changed";
  return "retry";
}
export function appendAccountBlockRecovery({ root, error, onRecover, onRetry }) {
  const kind = accountBlockRecoveryKind(error);
  const action = button(kind === "sign-in" ? "Sign in" : kind === "account-changed" ? "Reopen account" : "Retry", "recover");
  action.disabled = navigator.onLine === false;
  action.onclick = () => {
    if (kind === "retry") { onRetry?.(); return; }
    if (onRecover) { onRecover(kind); return; }
    if (kind === "sign-in") {
      const next = window.location.pathname + window.location.search;
      window.location.assign("/login/?next=" + encodeURIComponent(next));
    } else window.location.reload();
  };
  root.append(action);
}
function key() { return "account-block:" + crypto.randomUUID(); }
function assertState(data, owner, targetRef = null) {
  if (data?.ok !== true || String(data.accountUsername).toLowerCase() !== owner.toLowerCase()
      || data.targetKind !== "epic_account" || typeof data.targetRef !== "string"
      || !/^epic_[A-Za-z0-9_-]{8,240}$/.test(data.targetRef)
      || targetRef && data.targetRef !== targetRef
      || typeof data.blocked !== "boolean" || typeof data.interactionAllowed !== "boolean"
      || !Number.isSafeInteger(data.revision) || data.revision < 0) throw new Error("The selected account changed. Reopen its block controls.");
  return data;
}
export async function webBlockRequest(path, options = {}) {
  const response = await fetch("/api" + path, { credentials: "same-origin", cache: "no-store", ...options });
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.ok !== true) throw Object.assign(new Error(data?.error?.message || data?.error || data?.message || "Account blocking is unavailable. Please retry."), { status: response.status, code: data?.code });
  return data;
}

/** Mount a confirmed Block/Unblock action for exactly one EPIC account. */
export function mountAccountBlockButton({ root, accountUsername, targetUsername, targetRef = null, request = webBlockRequest, onChange = () => {}, onRecover }) {
  if (!root || !safeUsername(accountUsername) || (!targetRef && !safeUsername(targetUsername))) throw new Error("Select an EPIC account first.");
  const owner = accountUsername;
  const base = `/accounts/${encodeURIComponent(owner)}/blocks`;
  let state = null, csrfToken = "", disposed = false, busy = false, confirming = false, sequence = 0;
  let abort = null, notice = "", error = false, recoveryError = null;
  function paint() {
    if (disposed) return;
    root.replaceChildren(); root.classList.add("epic-account-block");
    root.setAttribute("aria-busy", String(busy));
    const status = node("p", notice || (state?.blocked ? "You blocked this account." : state && !state.interactionAllowed ? "Interaction with this account is unavailable." : ""), { role: error ? "alert" : "status" });
    root.append(status);
    if (error && recoveryError) {
      appendAccountBlockRecovery({ root, error: recoveryError, onRecover, onRetry: () => void load() });
      return;
    }
    if (confirming && state) {
      const label = state.username ? `@${state.username}` : "this account";
      root.append(node("p", state.blocked ? `Unblock ${label} in EPIC?` : `Block ${label} in EPIC? You will no longer be able to exchange messages with this account.`));
      const cancel = button("Cancel", "cancel"), confirm = button(state.blocked ? "Unblock account" : "Block account", "confirm");
      cancel.disabled = busy; confirm.disabled = busy || navigator.onLine === false;
      cancel.onclick = () => { confirming = false; paint(); root.querySelector("button")?.focus(); };
      confirm.onclick = () => void mutate();
      root.append(cancel, confirm); return;
    }
    if (state) {
      const action = button(busy ? "Saving…" : state.blocked ? "Unblock account" : "Block account", "open");
      action.disabled = busy || navigator.onLine === false || (!state.available && !state.blocked);
      action.onclick = () => { confirming = true; notice = ""; error = false; paint(); root.querySelector("button")?.focus(); };
      root.append(action);
    }
    const refresh = button(busy ? "Loading…" : "Refresh block status", "refresh");
    refresh.disabled = busy || navigator.onLine === false;
    refresh.onclick = () => void load(); root.append(refresh);
  }
  function newRequest() { abort?.abort(); abort = new AbortController(); return { id: ++sequence, signal: abort.signal }; }
  function valid(id) { return !disposed && id === sequence; }
  async function load() {
    const operation = newRequest(); busy = true; confirming = false; notice = "Checking block status…"; error = false; recoveryError = null; paint();
    try {
      const path = targetRef ? `${base}/${encodeURIComponent(targetRef)}` : `${base}/resolve/${encodeURIComponent(targetUsername)}`;
      const data = assertState(await request(path, { signal: operation.signal, headers: { "X-EPIC-Expected-Account": owner } }), owner, targetRef);
      if (!valid(operation.id)) return;
      state = data; targetRef = data.targetRef; csrfToken = data.csrfToken || ""; notice = "";
      onChange({ ...state });
    } catch (reason) { if (valid(operation.id)) { state = null; csrfToken = ""; notice = failure(reason); error = true; recoveryError = reason; } }
    finally { if (valid(operation.id)) { busy = false; paint(); } }
  }
  async function mutate() {
    if (busy || !state || !confirming || navigator.onLine === false) return;
    const before = state, desired = !before.blocked, operation = newRequest();
    busy = true; notice = desired ? "Blocking account…" : "Unblocking account…"; error = false; paint();
    try {
      const data = assertState(await request(`${base}/${encodeURIComponent(before.targetRef)}`, {
        method: desired ? "PUT" : "DELETE", signal: operation.signal,
        headers: { "X-EPIC-Expected-Account": owner, "X-EPIC-CSRF": csrfToken, "If-Match": `"${before.revision}"`, "Idempotency-Key": key() }
      }), owner, before.targetRef);
      if (!valid(operation.id)) return;
      if (data.blocked !== desired) throw new Error("The block changed elsewhere. Refresh to confirm its current state.");
      state = data; notice = desired ? "Account blocked in EPIC." : "Your block was removed."; confirming = false;
      onChange({ ...state });
      window.dispatchEvent(new CustomEvent("epic:account-block-changed", { detail: { accountUsername: owner, ...state } }));
    } catch (reason) {
      if (valid(operation.id)) { state = null; confirming = false; notice = `${failure(reason)} Refresh to confirm whether the change was saved.`; error = true; recoveryError = reason; csrfToken = ""; }
    } finally { if (valid(operation.id)) { busy = false; paint(); root.querySelector("button")?.focus(); } }
  }
  function connectivity() { if (!busy) void load(); }
  function visibility() { if (document.visibilityState === "visible") connectivity(); }
  function changed(event) { if (event.detail?.accountUsername?.toLowerCase() === owner.toLowerCase() && event.detail.targetRef === targetRef && !busy) void load(); }
  window.addEventListener("online", connectivity); window.addEventListener("offline", connectivity);
  document.addEventListener("visibilitychange", visibility);
  window.addEventListener("epic:account-block-changed", changed);
  void load();
  return { refresh: load, dispose() {
    disposed = true; ++sequence; abort?.abort(); root.replaceChildren();
    window.removeEventListener("online", connectivity); window.removeEventListener("offline", connectivity);
    document.removeEventListener("visibilitychange", visibility); window.removeEventListener("epic:account-block-changed", changed);
  } };
}

/** A small account lookup and persisted blocked list, usable without Inbox access. */
export function mountBlockedAccountsManager({ root, accountUsername, request = webBlockRequest, onRecover }) {
  if (!root || !safeUsername(accountUsername)) throw new Error("Sign in and select an account first.");
  let disposed = false, abort = null, sequence = 0, nextCursor = null, loading = false, candidate = null;
  const children = [];
  const recovery = node("div");
  root.replaceChildren(); root.classList.add("epic-blocked-accounts");
  const heading = node("h2", "Blocked accounts"), owner = node("p", `For @${accountUsername}`);
  const form = node("form"), label = node("label", "EPIC account username "), input = node("input", undefined, { type: "text", name: "username", maxlength: "120", autocomplete: "off", required: "", placeholder: "username" });
  label.append(input); const find = button("Find account", "find"); find.type = "submit"; form.append(label, find);
  const selected = node("section", undefined, { "aria-label": "Selected account" });
  const refresh = button("Refresh blocked accounts", "refresh-list"), status = node("p", "", { role: "status" });
  const list = node("div", undefined, { "aria-label": "Your blocked accounts" }), more = button("Load more", "more"); more.hidden = true;
  root.append(heading, owner, form, selected, refresh, status, recovery, list, more);
  form.onsubmit = event => {
    event.preventDefault(); candidate?.dispose(); selected.replaceChildren();
    const username = input.value.trim().replace(/^@/, "");
    if (!safeUsername(username) || username.toLowerCase() === accountUsername.toLowerCase()) { selected.append(node("p", "Enter another EPIC account's username.", { role: "alert" })); return; }
    selected.append(node("h3", `@${username}`)); const controls = node("div"); selected.append(controls);
    candidate = mountAccountBlockButton({ root: controls, accountUsername, targetUsername: username, request, onRecover });
  };
  async function load(append = false) {
    if (disposed || loading || navigator.onLine === false) { if (navigator.onLine === false) status.textContent = "Reconnect to manage blocked accounts."; return; }
    abort?.abort(); abort = new AbortController(); const id = ++sequence;
    loading = true; refresh.disabled = true; more.disabled = true; recovery.replaceChildren(); status.setAttribute("role", "status"); status.textContent = "Loading blocked accounts…";
    try {
      const path = `/accounts/${encodeURIComponent(accountUsername)}/blocks` + (append && nextCursor ? `?after=${encodeURIComponent(nextCursor)}` : "");
      const data = await request(path, { signal: abort.signal, headers: { "X-EPIC-Expected-Account": accountUsername } });
      if (disposed || id !== sequence) return;
      if (data?.ok !== true || data.accountUsername?.toLowerCase() !== accountUsername.toLowerCase() || !Array.isArray(data.items)
          || data.nextCursor != null && !/^accountblock_[a-f0-9]{64}$/.test(data.nextCursor)) throw new Error("The blocked-account list could not be verified.");
      if (!append) { children.splice(0).forEach(item => item.dispose()); list.replaceChildren(); }
      for (const item of data.items) {
        assertState({ ...item, ok: true, accountUsername }, accountUsername);
        if (item.blocked !== true) throw new Error("Refresh the blocked-account list.");
        const entry = node("section"); entry.append(node("h3", item.username ? `@${item.username}` : "Unavailable account"));
        const controls = node("div"); entry.append(controls); list.append(entry);
        children.push(mountAccountBlockButton({ root: controls, accountUsername, targetRef: item.targetRef, request, onRecover }));
      }
      input.disabled = false; find.disabled = false; nextCursor = data.nextCursor || null; more.hidden = !nextCursor;
      status.textContent = list.children.length ? "" : "You have not blocked any accounts.";
    } catch (reason) { if (!disposed && id === sequence) {
      status.textContent = failure(reason); status.setAttribute("role", "alert");
      if (accountBlockRecoveryKind(reason) !== "retry") {
        candidate?.dispose(); candidate = null; selected.replaceChildren();
        children.splice(0).forEach(item => item.dispose()); list.replaceChildren();
        nextCursor = null; more.hidden = true; input.disabled = true; find.disabled = true;
      }
      appendAccountBlockRecovery({ root: recovery, error: reason, onRecover, onRetry: () => void load() });
    } }
    finally { if (!disposed && id === sequence) { loading = false; refresh.disabled = false; more.disabled = false; } }
  }
  refresh.onclick = () => void load(); more.onclick = () => void load(true);
  function changed(event) { if (event.detail?.accountUsername?.toLowerCase() === accountUsername.toLowerCase()) void load(); }
  function online() { void load(); }
  window.addEventListener("epic:account-block-changed", changed); window.addEventListener("online", online);
  void load();
  return { refresh: () => load(), dispose() {
    disposed = true; ++sequence; abort?.abort(); candidate?.dispose(); children.forEach(item => item.dispose()); root.replaceChildren();
    window.removeEventListener("epic:account-block-changed", changed); window.removeEventListener("online", online);
  } };
}
